import React from "react";
import { createPortal } from "react-dom";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  ActionButton,
  joinClasses,
} from "../ResultsPrimitives.jsx";
import { resolveDispatchDayStatus } from "../../utils/dispatchDayControls.js";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { focusCalendarDay, isShortcutTypingTarget } from "../../utils/dispatchDayCalendarUtils.js";
import {
  buildDispatchCalendarCells,
  buildDispatchMonthGroups,
  buildDispatchStatusPills,
  buildDispatchStatusSummary,
  createDispatchCalendarKeyDownHandler,
  DispatchDayCalendarPopover,
  DispatchInlineCalendarPreview,
  formatReadableDispatchDate,
  getLocalTodayDispatchDate,
  normalizeDispatchMonthKey,
  resolveDefaultDispatchMonthKey,
} from "./DispatchDayCalendarPopover.jsx";

const TRIGGER_DATE_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function parseDispatchDate(value) {
  const normalizedValue = normalizeText(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedDate = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (
    Number.isNaN(parsedDate.getTime())
    || parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return null;
  }

  return parsedDate;
}

function formatTriggerDispatchDate(dispatchDate, fallbackLabel = "") {
  const parsedDate = parseDispatchDate(dispatchDate);
  if (!parsedDate) {
    return normalizeText(fallbackLabel) || normalizeText(dispatchDate) || "Choisissez une journée";
  }

  return TRIGGER_DATE_LABEL_FORMATTER.format(parsedDate);
}

function buildTriggerSupportingText(activeStatus = null, { hasExplicitSelection = false } = {}) {
  if (!hasExplicitSelection) {
    return "Choisissez la journée qui pilote l'affectation et l'optimisation.";
  }

  const visualStatusKey = activeStatus?.legacyKey || "";

  if (visualStatusKey === "closed") {
    return "";
  }

  if (visualStatusKey === "optimized") {
    return "";
  }

  if (visualStatusKey === "inconsistent") {
    return "";
  }

  return "";
}

function getStateBadgeClassName(item = null) {
  const status = resolveDispatchDayStatus(item);
  const visualStatusKey = status?.legacyKey || "unavailable";

  if (visualStatusKey === "inconsistent") {
    return "border-[var(--dispatch-status-warning-border)] bg-[var(--dispatch-status-warning-fill)] text-[#9a670f]";
  }

  if (visualStatusKey === "closed") {
    return "border-[rgba(197,82,74,0.18)] bg-[rgba(254,242,242,0.96)] text-[#a03a31]";
  }

  if (visualStatusKey === "optimized") {
    return "border-[var(--dispatch-status-success-border)] bg-[var(--dispatch-status-success-fill)] text-[#0f766e]";
  }

  return "border-[var(--dispatch-status-neutral-border)] bg-[var(--dispatch-status-neutral-fill)] text-slate-600";
}

export default function DispatchDayPicker({
  controls = null,
  interactions = {},
  showInlineCalendarPreview = false,
  inlinePreviewFooter = null,
  inlinePreviewLayout = "split",
  showDayStateActions = true,
  compact = false,
  showStepButtons = false,
  showTriggerStatus = true,
}) {
  if (!controls || typeof controls !== "object") {
    return null;
  }

  const items = Array.isArray(controls.items)
    ? controls.items.filter(Boolean).map((item) => ({
      ...item,
      dispatchDate: normalizeText(item?.dispatchDate),
      label: normalizeText(item?.label),
    }))
    : [];

  if (!items.length) {
    return null;
  }

  const activeDispatchDate = normalizeText(controls.activeDispatchDate);
  const activeDispatchMonth = normalizeDispatchMonthKey(controls.activeDispatchMonth);
  const activeDispatchState = normalizeText(controls.activeDispatchState) || "open";
  const hiddenClosedCount = Number(controls.hiddenClosedCount) || 0;
  const showArchive = controls.showArchive === true;
  const selectedLabel = normalizeText(controls.selectedLabel) || activeDispatchDate || "Journée active";
  const hasExplicitSelection = activeDispatchDate.length > 0;
  const activeItem = hasExplicitSelection
    ? items.find((item) => item.dispatchDate === activeDispatchDate)
      || items.find((item) => item.isActive)
      || null
    : null;
  const canCloseActiveDay = interactions.canCloseDispatchDay === true
    && activeItem?.hasDispatch === true
    && activeDispatchState !== "closed";
  const canReopenActiveDay = interactions.canReopenDispatchDay === true
    && Boolean(activeDispatchDate)
    && activeDispatchState === "closed";
  const monthGroups = React.useMemo(() => buildDispatchMonthGroups(items), [items]);
  const todayDispatchDate = React.useMemo(() => getLocalTodayDispatchDate(), []);
  const defaultMonthKey = React.useMemo(() => resolveDefaultDispatchMonthKey({
    activeDate: activeDispatchDate,
    activeDispatchMonth,
    items,
  }), [activeDispatchDate, activeDispatchMonth, items]);
  const [activeMonthKey, setActiveMonthKey] = React.useState(defaultMonthKey);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  React.useEffect(() => {
    setIsPopoverOpen(false);
  }, [activeDispatchDate, interactions.activeDetailView]);
  const activeMonthGroup = monthGroups.find((group) => group.key === activeMonthKey) || monthGroups[0] || null;
  const calendarCells = React.useMemo(() => buildDispatchCalendarCells(activeMonthGroup), [activeMonthGroup]);
  const anchorRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const dayButtonRefs = React.useRef([]);
  const popoverId = React.useId();
  const titleId = React.useId();
  const isStackedInlinePreview = showInlineCalendarPreview && inlinePreviewLayout === "stack";
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isPopoverOpen,
    placement: isStackedInlinePreview ? "bottom-end" : "bottom-start",
    middleware: [
      offset(9),
      flip({ padding: 16 }),
      shift({ padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  React.useEffect(() => {
    if (!monthGroups.some((group) => group.key === activeMonthKey)) {
      setActiveMonthKey(defaultMonthKey);
    }
  }, [activeMonthKey, defaultMonthKey, monthGroups]);

  React.useEffect(() => {
    if (!defaultMonthKey || isPopoverOpen) {
      return;
    }

    setActiveMonthKey(defaultMonthKey);
  }, [defaultMonthKey, isPopoverOpen]);

  React.useEffect(() => {
    refs.setReference(anchorRef.current);
  }, [isPopoverOpen, refs]);

  React.useEffect(() => {
    if (!isPopoverOpen || !isPositioned || typeof window === "undefined") {
      return undefined;
    }

    const activeCellIndex = calendarCells.findIndex((cell) => cell?.item?.dispatchDate === activeDispatchDate);
    const firstFocusableIndex = activeCellIndex >= 0
      ? activeCellIndex
      : calendarCells.findIndex((cell) => cell?.item);

    if (firstFocusableIndex < 0) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      focusCalendarDay(dayButtonRefs.current[firstFocusableIndex]);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeDispatchDate, calendarCells, isPopoverOpen, isPositioned]);

  usePopoverDismiss({
    anchorElement: anchorRef.current,
    floatingRef: refs.floating,
    isOpen: isPopoverOpen,
    onClose() {
      setIsPopoverOpen(false);
    },
  });

  const handleSelectDispatchDay = (dispatchDate) => {
    const safeDispatchDate = normalizeText(dispatchDate);
    if (!safeDispatchDate) {
      return;
    }

    setIsPopoverOpen(false);
    interactions.onSelectDispatchDay?.(safeDispatchDate);
  };

  const sortedDispatchDates = items.map((item) => item.dispatchDate).filter(Boolean).sort();
  const previousDispatchDate = sortedDispatchDates.filter((date) => date < activeDispatchDate).at(-1) || "";
  const nextDispatchDate = sortedDispatchDates.find((date) => date > activeDispatchDate) || "";

  React.useEffect(() => {
    if (!showStepButtons || isPopoverOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const targetDate = event.key === "ArrowLeft" ? previousDispatchDate : event.key === "ArrowRight" ? nextDispatchDate : "";
      if (!targetDate || event.shiftKey || isShortcutTypingTarget(event)) {
        return;
      }
      event.preventDefault();
      interactions.onSelectDispatchDay?.(targetDate);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [interactions, isPopoverOpen, nextDispatchDate, previousDispatchDate, showStepButtons]);

  const renderStepButton = (targetDate, label, Icon) => (
    <button
      type="button"
      aria-label={label}
      title={targetDate ? `${label} : ${formatReadableDispatchDate(targetDate)}` : label}
      disabled={!targetDate}
      onClick={() => handleSelectDispatchDay(targetDate)}
      className="inline-flex w-8 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </button>
  );

  const handleSelectMonthKey = React.useCallback((monthKey) => {
    const safeMonthKey = normalizeDispatchMonthKey(monthKey);
    if (!safeMonthKey) {
      return;
    }

    setActiveMonthKey(safeMonthKey);
    interactions.onChangeDispatchMonth?.(safeMonthKey);
  }, [interactions]);

  const handleClosePopover = React.useCallback(({ restoreFocus = false } = {}) => {
    setIsPopoverOpen(false);

    if (!restoreFocus || typeof window === "undefined") {
      return;
    }

    window.requestAnimationFrame(() => {
      triggerRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const handleCalendarKeyDown = React.useMemo(
    () => createDispatchCalendarKeyDownHandler({
      calendarCells,
      dayButtonRefs,
      onClose: handleClosePopover,
    }),
    [calendarCells, handleClosePopover],
  );

  const activeStatusSource = activeItem || {
    dispatchDayState: activeDispatchState,
  };
  const activeStatus = hasExplicitSelection ? resolveDispatchDayStatus(activeStatusSource) : null;
  const activeStatusClassName = activeStatus ? getStateBadgeClassName(activeStatusSource) : "";
  const publishedPortalRouteCount = Number(interactions?.portalPublications?.summary?.publishedCount) || 0;
  const dispatchStatusSummary = React.useMemo(() => buildDispatchStatusSummary(items, {
    hiddenClosedCount,
    showArchive,
  }), [hiddenClosedCount, items, showArchive]);
  const triggerLabel = hasExplicitSelection
    ? formatTriggerDispatchDate(activeDispatchDate, selectedLabel)
    : "Choisissez une journée";
  const triggerSupportingText = buildTriggerSupportingText(activeStatus, { hasExplicitSelection });
  const dispatchStatusPills = React.useMemo(
    () => buildDispatchStatusPills(items, { hiddenClosedCount, showArchive }),
    [hiddenClosedCount, items, showArchive],
  );
  const isRibbonTriggerLayout = !compact && !showDayStateActions && !showInlineCalendarPreview;
  const showInlineStackedDayStateActions = showDayStateActions && isStackedInlinePreview && (canCloseActiveDay || canReopenActiveDay);
  const dayStateActionButtons = (
    <>
      {canCloseActiveDay ? (
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-7.75! min-h-7.75! rounded-sm! border-[rgba(217,119,6,0.2)]! bg-[rgba(255,247,237,0.94)]! px-2.75! text-[#9a670f]! shadow-none! hover:border-[rgba(217,119,6,0.28)]! hover:bg-[rgba(255,237,213,0.98)]! hover:text-alert-warning-ink!"
          onClick={() => {
            interactions.onCloseDispatchDay?.(activeDispatchDate);
          }}
        >
          Clore la journée
        </ActionButton>
      ) : null}

      {canReopenActiveDay ? (
        <ActionButton
          size="sm"
          tone="ghost"
          className="h-7.75! min-h-7.75! rounded-sm! border-[rgba(197,82,74,0.28)]! bg-[rgba(254,242,242,0.98)]! px-2.75! text-[#a03a31]! shadow-none! hover:border-[rgba(197,82,74,0.4)]! hover:bg-[rgba(254,226,226,0.98)]! hover:text-[#8f2f28]!"
          onClick={() => {
            interactions.onReopenDispatchDay?.(activeDispatchDate);
          }}
          title="Journée fermée — cliquez pour la rouvrir et réautoriser les modifications"
          aria-label="Journée fermée — Rouvrir la journée"
          data-dispatch-day-reopen-action="true"
          data-dispatch-day-closed-indicator="true"
        >
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 rounded-[3px] border border-[rgba(197,82,74,0.22)] bg-white/92 px-1.5 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.1em] text-[#a03a31]"
              aria-hidden="true"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#a03a31]" />
              Fermée
            </span>
            Rouvrir la journée
          </span>
        </ActionButton>
      ) : null}
    </>
  );

  return (
    <div className={joinClasses([
      "min-w-0 items-start gap-2",
      isRibbonTriggerLayout ? "h-full" : "",
      showInlineCalendarPreview
        ? (isStackedInlinePreview ? "grid gap-2" : "grid gap-3 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] xl:gap-4")
        : "flex flex-wrap justify-between",
    ])}>
      <div className={joinClasses([
        "grid min-w-0 gap-2",
        isRibbonTriggerLayout ? "h-full" : "",
      ])}>
        <div className={joinClasses([
          "min-w-0 items-start gap-2",
          isRibbonTriggerLayout ? "h-full" : "",
          showInlineStackedDayStateActions ? "grid grid-cols-[minmax(0,1fr)_auto]" : "grid",
        ])}>
          <div
            ref={anchorRef}
            className={joinClasses([
              "relative min-w-0 w-full",
              showStepButtons ? "flex items-stretch gap-1" : "",
              isRibbonTriggerLayout ? "h-full" : "",
              showInlineCalendarPreview
                ? (isStackedInlinePreview ? "max-w-none" : "max-w-none xl:max-w-104")
                : "max-w-152 flex-[1_1_34rem]",
            ])}
          >
            {showStepButtons ? renderStepButton(previousDispatchDate, "Jour précédent (←)", ChevronLeftIcon) : null}
            <button
              ref={triggerRef}
              type="button"
              aria-controls={popoverId}
              aria-expanded={isPopoverOpen}
              aria-haspopup="dialog"
              aria-label={compact ? `Journée de dispatch active ${triggerLabel} ${activeStatus?.label || ""}` : undefined}
              className={joinClasses([
                "group flex w-full min-w-0 items-center gap-1.75 rounded-[4px] border border-[var(--dispatch-day-trigger-border)] bg-[image:var(--dispatch-day-trigger-surface)] text-left shadow-none transition duration-150 hover:border-accent hover:bg-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
                compact ? "min-h-8.25 px-2 py-0.5" : isRibbonTriggerLayout ? "h-full min-h-17 px-3 py-2.75" : "min-h-11.5 px-2.5 py-1.5",
              ])}
              onClick={() => {
                setIsPopoverOpen((currentValue) => !currentValue);
              }}
            >
              <span className={joinClasses([
                "flex shrink-0 items-center justify-center rounded-[4px] border border-slate-200 bg-white text-slate-700 shadow-none",
                compact ? "h-7 w-7" : isRibbonTriggerLayout ? "h-9.5 w-9.5" : "h-7.5 w-7.5",
              ])}>
                <CalendarDaysIcon className={isRibbonTriggerLayout ? "h-4.5 w-4.5" : "h-4 w-4"} aria-hidden="true" />
              </span>

              <span className={joinClasses([
                "grid min-w-0 flex-1",
                isRibbonTriggerLayout ? "gap-1" : "gap-px",
              ])}>
                {!compact ? <span className="text-[0.56rem] font-black uppercase tracking-[0.14em] text-slate-500">{hasExplicitSelection ? "Journée de dispatch active" : "Journée de dispatch"}</span> : null}
                <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="truncate font-display text-[1rem] font-semibold tracking-[-0.03em] text-slate-900">{triggerLabel}</span>
                  {showTriggerStatus && activeStatus ? (
                    <span className={joinClasses([
                      "inline-flex items-center rounded-[4px] border px-2.25 py-0.75 text-[0.54rem] font-black uppercase tracking-[0.12em]",
                      activeStatusClassName,
                    ])}>
                      {activeStatus.label}
                    </span>
                  ) : null}
                  {publishedPortalRouteCount > 0 ? (
                    <span
                      className="inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-2.25 py-0.75 text-[0.54rem] font-black uppercase tracking-[0.12em] text-emerald-700"
                      data-dispatch-driver-portal-published
                    >
                      Portail {publishedPortalRouteCount}
                    </span>
                  ) : null}
                </span>
                {triggerSupportingText ? (
                  <span className="truncate text-[0.64rem] leading-4 text-slate-500">{triggerSupportingText}</span>
                ) : null}
              </span>

              <ChevronDownIcon
                className={joinClasses([
                  "h-4 w-4 shrink-0 text-slate-400 transition duration-150 group-hover:text-slate-700",
                  isPopoverOpen ? "rotate-180" : "rotate-0",
                ])}
                aria-hidden="true"
              />
            </button>
            {showStepButtons ? renderStepButton(nextDispatchDate, "Jour suivant (→)", ChevronRightIcon) : null}

            {isPopoverOpen && getReactPortalRoot() instanceof HTMLElement ? createPortal(
              <DispatchDayCalendarPopover
                activeDate={activeDispatchDate}
                activeMonthKey={activeMonthKey}
                align={isStackedInlinePreview ? "right" : "left"}
                controls={controls}
                dayButtonRefs={dayButtonRefs}
                floatingStyles={floatingStyles}
                isOpen={isPopoverOpen}
                isPositioned={isPositioned}
                isStackedInlinePreview={isStackedInlinePreview}
                onCalendarKeyDown={handleCalendarKeyDown}
                onClose={handleClosePopover}
                onSelectDate={handleSelectDispatchDay}
                onSelectMonthKey={handleSelectMonthKey}
                onToggleArchive={(nextShowArchive) => {
                  interactions.onToggleDispatchArchive?.(nextShowArchive);
                }}
                popoverId={popoverId}
                positioning="floating"
                setFloatingRef={refs.setFloating}
                showArchive={showArchive}
                titleId={titleId}
              />,
              getReactPortalRoot(),
            ) : null}
          </div>

          {showInlineStackedDayStateActions ? (
            <div className="flex flex-wrap items-center justify-end gap-1 pt-0.5 pr-0.5">
              {dayStateActionButtons}
            </div>
          ) : null}
        </div>

        {showDayStateActions && showInlineCalendarPreview && !isStackedInlinePreview && (canCloseActiveDay || canReopenActiveDay) ? (
          <div className="flex flex-wrap items-center justify-start gap-1 pr-0.5">
            {dayStateActionButtons}
          </div>
        ) : null}

        {showInlineCalendarPreview && inlinePreviewFooter ? (
          <div className="grid gap-2 rounded-lg border border-[rgba(148,163,184,0.16)] bg-[rgba(248,250,252,0.82)] p-3">
            {inlinePreviewFooter}
          </div>
        ) : null}
      </div>

      {showInlineCalendarPreview ? (
        <DispatchInlineCalendarPreview
          activeMonthGroup={activeMonthGroup}
          activeMonthKey={activeMonthKey}
          calendarCells={calendarCells}
          dispatchStatusPills={dispatchStatusPills}
          dispatchStatusSummary={dispatchStatusSummary}
          monthGroups={monthGroups}
          onSelectDispatchDay={handleSelectDispatchDay}
          onSelectMonthKey={handleSelectMonthKey}
          todayDispatchDate={todayDispatchDate}
        />
      ) : null}

      {!showInlineCalendarPreview && showDayStateActions && (canCloseActiveDay || canReopenActiveDay) ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 pr-0.5">
          {dayStateActionButtons}
        </div>
      ) : null}
    </div>
  );
}
